import { useState } from 'react';
import { useSignIn } from '../lib/session';
import { Banner, Button, Field } from '../components/ui';
import { CapMark, Wordmark } from '../components/Brand';

/**
 * The only unauthenticated screen in the application.
 *
 * There is no "create an account" link because there is no signup route to link
 * to — the Owner issues every login by hand. Saying so plainly is friendlier
 * than leaving someone hunting for a register button that does not exist.
 */
export function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const signIn = useSignIn();

  return (
    <div className="grid min-h-dvh lg:grid-cols-[1.1fr_1fr]">
      <div className="board relative hidden flex-col justify-between overflow-hidden p-12 lg:flex">
        <div className="flex items-center gap-2.5">
          <CapMark className="h-6 w-6 text-ochre-400" />
          <Wordmark tone="chalk" />
        </div>

        <div className="rise">
          <p className="sign text-[11px] tracking-[0.22em] text-ochre-400">
            Cold drinks, by the can
          </p>
          <h1 className="mt-4 max-w-md text-5xl leading-[1.05] text-chalk">
            Buy a little,
            <br />
            or buy the crate.
          </h1>
          <p className="mt-5 max-w-sm text-[15px] leading-relaxed text-chalk/65">
            Add one at a time, or take the whole pack. A pack has its own flat price, not a
            discount off the single — and the basket tells you when you are one tap away from it.
          </p>

          {/* The house offer, shown as the customer meets it: a total, not a rate. */}
          <div className="mt-10 grid max-w-sm grid-cols-3 gap-px overflow-hidden rounded-[3px] border board-rule border">
            {[
              { q: '1', p: '$20.00', deal: false },
              { q: '5', p: '$100.00', deal: false },
              { q: '10', p: '$150.00', deal: true },
            ].map((row) => (
              <div key={row.q} className="bg-slate-boardlight/60 px-3 py-4 text-center">
                <div className="sign text-[10px] text-chalk/50">{row.q}</div>
                <div
                  className={`tabular mt-1 text-lg ${row.deal ? 'text-ochre-400' : 'text-chalk'}`}
                >
                  {row.p}
                </div>
                {row.deal && <div className="sign mt-1 text-[9px] text-ochre-400">Deal</div>}
              </div>
            ))}
          </div>
        </div>

        <p className="text-[12px] text-chalk/40">
          House pricing. Each store sets its own, and the cheapest combination of packs and
          singles is worked out for you.
        </p>
      </div>

      <div className="flex items-center justify-center px-6 py-16">
        <form
          className="w-full max-w-sm rise"
          onSubmit={(event) => {
            event.preventDefault();
            signIn.mutate({ username: username.trim().toLowerCase(), password });
          }}
        >
          <div className="flex items-center gap-2.5 lg:hidden">
            <CapMark className="h-6 w-6 text-ochre-500" />
            <Wordmark />
          </div>

          <h2 className="mt-6 text-3xl text-ink-950 lg:mt-0">Sign in</h2>
          <p className="mt-2 text-[14px] text-ink-500">
            Accounts are created by the owner. There is no public registration.
          </p>

          <div className="mt-8 space-y-4">
            <Field
              label="Username"
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              required
              value={username}
              onChange={(event) => setUsername(event.target.value)}
            />
            <Field
              label="Password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>

          {signIn.isError && (
            <div className="mt-5">
              <Banner tone="error">{signIn.error.message}</Banner>
            </div>
          )}

          <Button
            type="submit"
            variant="primary"
            className="mt-6 w-full py-2.5"
            disabled={signIn.isPending}
          >
            {signIn.isPending ? 'Checking…' : 'Sign in'}
          </Button>

          <p className="mt-6 text-[13px] leading-relaxed text-ink-500">
            Forgotten your password? Ask the owner to set a new one — this shop sends no email, so
            there is no reset link to wait for.
          </p>
        </form>
      </div>
    </div>
  );
}
